// Argus — bulkDownload orchestrator. Bloc file-explorer-multiselect-download 2026-05-06.
//
// Constraint user: TOTUL prin WS, ZERO PHP/Node/Agent/DB changes. Reuse `wsClient.requestDirListing`
// + `wsClient.requestFileRead` existing.
//
// Algoritm:
// 1. showDirectoryPicker → user alege destination → FileSystemDirectoryHandle root.
// 2. Build queue inițial din selection (dir → enumDir task, file → downloadFile task).
// 3. Process cu concurrency 3 — Promise pool manual.
// 4. enumDir: requestDirListing recursive maxDepth=10 → enqueue tasks descendenți.
// 5. downloadFile: requestFileRead → decode base64 → write via FileSystemFileHandle (recursive
//    getDirectoryHandle pentru parent path).
// 6. Skip files >10 MB cu reason FILE_TOO_LARGE_FOR_WS_PENDING_BATCH3.

import { wsClient, WsFileTooLargeError, type DirListingEntry } from './wsClient';
import { joinPath } from './fsEntries';

export const MAX_FILE_BYTES_INLINE = 10 * 1024 * 1024;
const CONCURRENCY = 3;
const ENUM_MAX_DEPTH = 10;

export class BulkDownloadCancelled extends Error {
  constructor() { super('Bulk download cancelled'); this.name = 'BulkDownloadCancelled'; }
}

export class BulkDownloadUnsupportedBrowser extends Error {
  constructor() { super('Browser nu suportă File System Access API'); this.name = 'BulkDownloadUnsupportedBrowser'; }
}

export interface DownloadItem {
  /** Cale relativă față de currentPath (ex: "subdir/file.txt"). */
  readonly relativePath: string;
  /** Cale absolută agent (ex: "C:\\Users\\X\\subdir\\file.txt"). */
  readonly fullPath: string;
  readonly isDir: boolean;
  /** Doar pentru file. null pentru dir. */
  readonly size: number | null;
}

export interface FileTaskState {
  readonly relativePath: string;
  readonly fullPath: string;
  readonly size: number;
  status: 'queued' | 'in_progress' | 'done' | 'failed' | 'skipped' | 'cancelled';
  reason?: string;
  bytesWritten?: number;
}

export interface BulkDownloadProgress {
  readonly fileTasks: ReadonlyMap<string, FileTaskState>; // key = fullPath
  readonly enumeratingDirs: number;
  readonly totalFiles: number;
  readonly doneFiles: number;
  readonly failedFiles: number;
  readonly downloadedBytes: number;
  readonly totalBytes: number;
  readonly status: 'enumerating' | 'downloading' | 'done' | 'cancelled' | 'error';
  readonly errorMessage?: string | undefined;
}

export interface BulkDownloadOptions {
  agentId: string;
  /** Path-ul folderului din care provin items (folosit pt calc relativePath dacă lipsește). */
  basePath: string;
  items: readonly DownloadItem[];
  onProgress: (p: BulkDownloadProgress) => void;
  abortSignal: AbortSignal;
}

interface EnumTask { type: 'enumDir'; relativePath: string; fullPath: string; }
interface FileTask { type: 'downloadFile'; relativePath: string; fullPath: string; size: number; }
type Task = EnumTask | FileTask;

interface FileSystemDirectoryHandleApi {
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FileSystemDirectoryHandleApi>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileSystemFileHandleApi>;
}
interface FileSystemFileHandleApi {
  createWritable(): Promise<FileSystemWritableFileStreamApi>;
}
interface FileSystemWritableFileStreamApi {
  write(data: BlobPart): Promise<void>;
  close(): Promise<void>;
}

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandleApi>;
  }
}

export async function bulkDownload(options: BulkDownloadOptions): Promise<BulkDownloadProgress> {
  const { agentId, items, onProgress, abortSignal } = options;

  if (typeof window.showDirectoryPicker !== 'function') {
    throw new BulkDownloadUnsupportedBrowser();
  }

  // Permission prompt browser. Cancel → DOMException AbortError.
  let rootHandle: FileSystemDirectoryHandleApi;
  try {
    rootHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
  } catch (err) {
    throw new BulkDownloadCancelled();
  }

  const fileTasks = new Map<string, FileTaskState>();
  let enumeratingDirs = 0;
  let totalFiles = 0;
  let doneFiles = 0;
  let failedFiles = 0;
  let downloadedBytes = 0;
  let totalBytes = 0;
  let status: BulkDownloadProgress['status'] = 'enumerating';
  let errorMessage: string | undefined;

  const emit = (): void => {
    onProgress({
      fileTasks: new Map(fileTasks),
      enumeratingDirs,
      totalFiles,
      doneFiles,
      failedFiles,
      downloadedBytes,
      totalBytes,
      status,
      errorMessage,
    });
  };

  // Queue + în-flight pool manual (concurrency=3).
  const queue: Task[] = [];
  for (const item of items) {
    if (abortSignal.aborted) break;
    if (item.isDir) {
      queue.push({ type: 'enumDir', relativePath: item.relativePath, fullPath: item.fullPath });
    } else {
      const size = item.size ?? 0;
      queue.push({ type: 'downloadFile', relativePath: item.relativePath, fullPath: item.fullPath, size });
      registerFileTask(item.fullPath, item.relativePath, size);
    }
  }

  function registerFileTask(fullPath: string, relativePath: string, size: number): void {
    if (fileTasks.has(fullPath)) return;
    fileTasks.set(fullPath, { fullPath, relativePath, size, status: 'queued' });
    totalFiles++;
    totalBytes += size;
  }

  function updateFileTask(fullPath: string, patch: Partial<FileTaskState>): void {
    const existing = fileTasks.get(fullPath);
    if (!existing) return;
    fileTasks.set(fullPath, { ...existing, ...patch });
  }

  emit();

  async function processEnumDir(task: EnumTask): Promise<void> {
    enumeratingDirs++;
    emit();
    try {
      const result = await wsClient.requestDirListing(agentId, task.fullPath, {
        recursive: true,
        maxDepth: ENUM_MAX_DEPTH,
      });
      // Pentru fiecare entry: enqueue task copil cu relativePath nested.
      for (const entry of result.entries) {
        if (abortSignal.aborted) return;
        const childRelative = task.relativePath
          ? `${task.relativePath}/${normalizeRelative(entry.relativePath)}`
          : normalizeRelative(entry.relativePath);
        const childFull = joinPath(task.fullPath, entry.relativePath.replace(/^[\\/]/, ''));
        if (entry.type === 'dir') {
          // recursive=true returnează deja descendenții — NU mai re-enqueue dirs
          // (altfel duplicate enumeration). Doar ensure folder local creat când file copil ajunge la write.
        } else {
          const size = entry.size ?? 0;
          if (!fileTasks.has(childFull)) {
            registerFileTask(childFull, childRelative, size);
            queue.push({ type: 'downloadFile', relativePath: childRelative, fullPath: childFull, size });
          }
        }
      }
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'enumDir failed';
      // Folder-ul ca atare nu are file task — adăugăm un sentinel cu fullPath = relativePath.
      fileTasks.set(`__DIR__:${task.fullPath}`, {
        fullPath: task.fullPath,
        relativePath: task.relativePath,
        size: 0,
        status: 'failed',
        reason: `Enumerate failed: ${reason}`,
      });
      failedFiles++;
    } finally {
      enumeratingDirs--;
      emit();
    }
  }

  async function processDownloadFile(task: FileTask): Promise<void> {
    if (abortSignal.aborted) {
      updateFileTask(task.fullPath, { status: 'cancelled' });
      emit();
      return;
    }
    updateFileTask(task.fullPath, { status: 'in_progress' });
    emit();
    try {
      // Build path local: split relativePath in segments + ensure parent dirs.
      const segments = task.relativePath.split(/[\\/]/).filter((s) => s.length > 0);
      const fileName = segments.pop();
      if (!fileName) throw new Error(`Invalid relativePath: ${task.relativePath}`);

      if (task.size <= MAX_FILE_BYTES_INLINE) {
        // ≤10 MB: inline single requestFileRead (path optimal existing).
        const result = await wsClient.requestFileRead(agentId, task.fullPath, '');
        if (abortSignal.aborted) {
          updateFileTask(task.fullPath, { status: 'cancelled' });
          emit();
          return;
        }
        const bytes = base64ToBytes(result.content);
        let dirHandle = rootHandle;
        for (const seg of segments) {
          dirHandle = await dirHandle.getDirectoryHandle(sanitizeName(seg), { create: true });
        }
        const fileHandle = await dirHandle.getFileHandle(sanitizeName(fileName), { create: true });
        const writable = await fileHandle.createWritable();
        const ab: ArrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
        await writable.write(new Blob([ab], { type: result.mimeType }));
        await writable.close();
        updateFileTask(task.fullPath, { status: 'done', bytesWritten: bytes.length });
        doneFiles++;
        downloadedBytes += bytes.length;
        emit();
      } else {
        // Bloc ws-direct-batch-3: >10 MB chunked download via file_read_chunk loop.
        let dirHandle = rootHandle;
        for (const seg of segments) {
          dirHandle = await dirHandle.getDirectoryHandle(sanitizeName(seg), { create: true });
        }
        const fileHandle = await dirHandle.getFileHandle(sanitizeName(fileName), { create: true });
        const writable = await fileHandle.createWritable();
        let chunkOffset = 0;
        let totalSize = task.size;
        const CHUNK_SIZE = 1024 * 1024; // 1 MB
        let bytesWrittenLocal = 0;
        try {
          while (chunkOffset < totalSize && !abortSignal.aborted) {
            const justification = chunkOffset === 0 ? '' : undefined;
            const chunk = await wsClient.requestFileChunk(agentId, task.fullPath, chunkOffset, CHUNK_SIZE, justification);
            if (abortSignal.aborted) break;
            const chunkBytes = base64ToBytes(chunk.data);
            const ab: ArrayBuffer = chunkBytes.buffer.slice(chunkBytes.byteOffset, chunkBytes.byteOffset + chunkBytes.byteLength) as ArrayBuffer;
            await writable.write(new Blob([ab], { type: chunk.mimeType }));
            chunkOffset += chunk.size;
            totalSize = chunk.totalSize; // refresh în caz file changed.
            bytesWrittenLocal += chunk.size;
            updateFileTask(task.fullPath, { bytesWritten: bytesWrittenLocal });
            downloadedBytes += chunk.size;
            emit();
            if (chunk.eof) break;
          }
        } finally {
          await writable.close();
        }
        if (abortSignal.aborted) {
          updateFileTask(task.fullPath, { status: 'cancelled' });
        } else {
          updateFileTask(task.fullPath, { status: 'done', bytesWritten: bytesWrittenLocal });
          doneFiles++;
        }
        emit();
      }
    } catch (err) {
      let reason: string;
      if (err instanceof WsFileTooLargeError) {
        reason = 'FILE_TOO_LARGE_FOR_WS (chunked path failed)';
      } else {
        reason = err instanceof Error ? err.message : 'download failed';
      }
      updateFileTask(task.fullPath, { status: 'failed', reason });
      failedFiles++;
      emit();
    }
  }

  // Pool concurrency.
  const inFlight: Promise<void>[] = [];

  async function runOne(): Promise<void> {
    while (queue.length > 0) {
      if (abortSignal.aborted) break;
      const task = queue.shift();
      if (!task) break;
      if (task.type === 'enumDir') {
        await processEnumDir(task);
      } else {
        await processDownloadFile(task);
      }
    }
  }

  // Faza 1: enumerate dirs serial (depth-first pentru predictibilitate progress).
  // Faza 2: download files paralele.
  // Pentru simplicitate iter1: combinat în pool — ordering best-effort.
  status = 'downloading';
  for (let i = 0; i < CONCURRENCY; i++) {
    inFlight.push(runOne());
  }

  await Promise.all(inFlight);

  if (abortSignal.aborted) {
    status = 'cancelled';
    // Mark queued tasks as cancelled.
    for (const [k, task] of fileTasks) {
      if (task.status === 'queued' || task.status === 'in_progress') {
        fileTasks.set(k, { ...task, status: 'cancelled' });
      }
    }
  } else {
    status = 'done';
  }
  emit();

  return {
    fileTasks: new Map(fileTasks),
    enumeratingDirs,
    totalFiles,
    doneFiles,
    failedFiles,
    downloadedBytes,
    totalBytes,
    status,
    errorMessage,
  };
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function normalizeRelative(rel: string): string {
  return rel.replace(/^[\\/]+/, '').replace(/[\\/]/g, '/');
}

/** Sanitize segment nume — Windows allows backslash dar FS Access API NU. */
function sanitizeName(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_');
}

/** Helper: compute DownloadItem din FsEntry array + currentPath. */
export function buildDownloadItems(
  selectedNames: ReadonlySet<string>,
  entries: readonly { name: string; isDir: boolean; size?: number | null }[],
  currentPath: string,
): DownloadItem[] {
  const items: DownloadItem[] = [];
  for (const entry of entries) {
    if (!selectedNames.has(entry.name)) continue;
    const fullPath = joinPath(currentPath, entry.name);
    items.push({
      relativePath: entry.name,
      fullPath,
      isDir: entry.isDir,
      size: entry.size ?? null,
    });
  }
  return items;
}
// Suppress unused import warning — DirListingEntry type referit indirect.
export type { DirListingEntry };
